import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Autocomplete,
  LinearProgress,
  IconButton,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import {
  useFeedAssignments,
  useFeedTypes,
  useFeedBrands,
  useAssignFeed,
  useUpdateFeedAssignment,
  useDeleteFeedAssignment,
  useDeactivateFeedAssignment,
  useFeedProcurements,
  useFeedProductions,
} from '../hooks/useFeed';
import { useBirds } from '../hooks/useBirds';

const FeedBatchAssignmentsPage: React.FC = () => {
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDeactivateDialog, setOpenDeactivateDialog] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<any>(null);
  const [assignmentToDelete, setAssignmentToDelete] = useState<any>(null);
  const [assignmentToDeactivate, setAssignmentToDeactivate] = useState<any>(null);
  const [showAllAssignments, setShowAllAssignments] = useState(false);
  const [feedTypeFilter, setFeedTypeFilter] = useState<number | null>(null);
  const [feedBrandFilter, setFeedBrandFilter] = useState<number | null>(null);
  const [birdFilter, setBirdFilter] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning',
  });

  const {
    data: assignmentsData,
    isLoading,
    error,
    isFetching: assignmentsFetching,
    refetch,
  } = useFeedAssignments({ per_page: 1000 });

  const { data: feedTypes, isFetching: feedTypesFetching } = useFeedTypes();
  const { data: feedBrands, isFetching: feedBrandsFetching } = useFeedBrands();
  const { data: birdsData, isFetching: birdsFetching } = useBirds({ per_page: 1000 });
  const assignMutation = useAssignFeed();
  const updateMutation = useUpdateFeedAssignment();
  const deleteMutation = useDeleteFeedAssignment();
  const deactivateMutation = useDeactivateFeedAssignment();

  const assignments = useMemo(() => {
    let filtered = showAllAssignments
      ? assignmentsData?.data || []
      : (assignmentsData?.data || []).filter((assignment) => assignment.is_active);

    if (feedTypeFilter) {
      filtered = filtered.filter((assignment) => assignment.feed_type_id === feedTypeFilter);
    }

    if (feedBrandFilter) {
      filtered = filtered.filter((assignment) => assignment.feed_brand_id === feedBrandFilter);
    }

    if (birdFilter) {
      filtered = filtered.filter((assignment) => assignment.birds_id === birdFilter);
    }

    return filtered;
  }, [assignmentsData, showAllAssignments, feedTypeFilter, feedBrandFilter, birdFilter]);
  const birds = useMemo(() => birdsData?.data || [], [birdsData]);

  const [assignForm, setAssignForm] = useState({
    birds_id: '',
    feed_type_id: '',
    feed_brand_id: '',
    consumption_kg: '',
    consumption_period: 'weekly',
    allocation_multiplier: '1',
    notes: '',
    source: { name: 'Auto (aggregate stock)', id: 'auto' },
    feed_procurement_id: '',
    feed_production_id: '',
    assigned_date: new Date().toISOString().split('T')[0],
  });
  const [editForm, setEditForm] = useState({
    feed_type_id: '',
    feed_brand_id: '',
    consumption_kg: '',
    consumption_period: 'weekly',
    allocation_multiplier: '1',
    notes: '',
    source: { name: 'Auto (aggregate stock)', id: 'auto' },
    feed_procurement_id: '',
    feed_production_id: '',
    assigned_date: new Date().toISOString().split('T')[0],
  });

  const { data: feedProcurements, isFetching: feedProcurementsFetching } = useFeedProcurements({
    per_page: 100,
    feed_brand_id: assignForm.feed_brand_id ? parseInt(assignForm.feed_brand_id) : undefined,
    feed_type_id: assignForm.feed_type_id ? parseInt(assignForm.feed_type_id) : undefined,
  });
  const { data: feedProductions, isFetching: feedProductionsFetching } = useFeedProductions({
    feed_brand_id: assignForm.feed_brand_id ? parseInt(assignForm.feed_brand_id) : undefined,
    feed_type_id: assignForm.feed_type_id ? parseInt(assignForm.feed_type_id) : undefined,
  });

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth="500px">
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Failed to Load Feed Assignments Data
            </Typography>
            <Typography variant="body2" paragraph>
              {error instanceof Error
                ? error.message
                : 'An error occurred while fetching feed assignments. Please try again.'}
            </Typography>
          </Alert>
          <Button variant="contained" onClick={() => refetch()} sx={{ color: '#fff' }}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAssign = async () => {
    if (
      !assignForm.birds_id ||
      !assignForm.feed_type_id ||
      !assignForm.feed_brand_id ||
      !assignForm.consumption_kg
    ) {
      showSnackbar('Please fill all required fields', 'warning');
      return;
    }

    const form = {
      birds_id: parseInt(assignForm.birds_id),
      feed_type_id: parseInt(assignForm.feed_type_id),
      feed_brand_id: parseInt(assignForm.feed_brand_id),
      consumption_kg:
        parseFloat(assignForm.consumption_kg) * parseFloat(assignForm.allocation_multiplier),
      consumption_period: assignForm.consumption_period as 'weekly' | 'daily',
      allocation_multiplier: parseFloat(assignForm.allocation_multiplier),
      notes: assignForm.notes || undefined,
      assigned_date: assignForm.assigned_date,
      feed_procurement_id:
        assignForm.source.id === 'procurement' && assignForm.feed_procurement_id
          ? parseInt(assignForm.feed_procurement_id)
          : undefined,
      feed_production_id:
        assignForm.source.id === 'production' && assignForm.feed_production_id
          ? parseInt(assignForm.feed_production_id)
          : undefined,
    };

    try {
      await assignMutation.mutateAsync(form);
      showSnackbar('Feed assigned successfully');
      setAssignForm({
        birds_id: '',
        feed_type_id: '',
        feed_brand_id: '',
        consumption_kg: '',
        consumption_period: 'weekly',
        allocation_multiplier: '1',
        notes: '',
        source: { name: 'Auto (aggregate stock)', id: 'auto' },
        feed_procurement_id: '',
        feed_production_id: '',
        assigned_date: new Date().toISOString().split('T')[0],
      });
      setOpenAssignDialog(false);
    } catch (error) {
      showSnackbar('Failed to assign feed', 'error');
    }
  };

  const handleEdit = (assignment: any) => {
    setEditingAssignment(assignment);
    setEditForm({
      feed_type_id: assignment.feed_type_id?.toString() || '',
      feed_brand_id: assignment.feed_brand_id?.toString() || '',
      consumption_kg: (assignment.consumption_period === 'daily'
        ? assignment.daily_consumption_kg
        : assignment.daily_consumption_kg * 7
      ).toString(),
      consumption_period: assignment.consumption_period || 'weekly',
      allocation_multiplier: '1',
      notes: assignment.notes || '',
      source: assignment.feed_procurement_id
        ? { name: 'Procured (select lot)', id: 'procurement' }
        : assignment.feed_production_id
          ? { name: 'Produced (in-house)', id: 'production' }
          : { name: 'Auto (aggregate stock)', id: 'auto' },
      feed_procurement_id: assignment.feed_procurement_id?.toString() || '',
      feed_production_id: assignment.feed_production_id?.toString() || '',
      assigned_date: assignment.assigned_date
        ? new Date(assignment.assigned_date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
    });
    setOpenEditDialog(true);
  };

  const handleUpdate = async () => {
    if (
      !editingAssignment ||
      !editForm.feed_type_id ||
      !editForm.feed_brand_id ||
      !editForm.consumption_kg
    ) {
      showSnackbar('Please fill all required fields', 'warning');
      return;
    }

    const form: any = {
      feed_type_id: parseInt(editForm.feed_type_id),
      feed_brand_id: parseInt(editForm.feed_brand_id),
      consumption_kg:
        parseFloat(editForm.consumption_kg) * parseFloat(editForm.allocation_multiplier),
      consumption_period: editForm.consumption_period as 'weekly' | 'daily',
      allocation_multiplier: parseFloat(editForm.allocation_multiplier),
      notes: editForm.notes || undefined,
      assigned_date: editForm.assigned_date,
    };

    if (editForm.source.id === 'procurement' && editForm.feed_procurement_id) {
      form.feed_procurement_id = parseInt(editForm.feed_procurement_id);
    }
    if (editForm.source.id === 'production' && editForm.feed_production_id) {
      form.feed_production_id = parseInt(editForm.feed_production_id);
    }

    try {
      await updateMutation.mutateAsync({ id: editingAssignment.id, data: form });
      showSnackbar('Feed assignment updated successfully');
      setOpenEditDialog(false);
      setEditingAssignment(null);
    } catch (error) {
      showSnackbar('Failed to update feed assignment', 'error');
    }
  };

  const handleDelete = async () => {
    if (!assignmentToDelete) return;
    try {
      await deleteMutation.mutateAsync(assignmentToDelete.id);
      showSnackbar('Feed assignment deleted successfully');
      setOpenDeleteDialog(false);
      setAssignmentToDelete(null);
    } catch (error) {
      showSnackbar('Failed to delete feed assignment', 'error');
    }
  };

  const handleDeactivate = async () => {
    if (!assignmentToDeactivate) return;
    try {
      await deactivateMutation.mutateAsync(assignmentToDeactivate.id);
      showSnackbar('Feed assignment deactivated successfully');
      setOpenDeactivateDialog(false);
      setAssignmentToDeactivate(null);
    } catch (error) {
      showSnackbar('Failed to deactivate feed assignment', 'error');
    }
  };
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      {(assignmentsFetching ||
        feedTypesFetching ||
        feedBrandsFetching ||
        birdsFetching ||
        feedProcurementsFetching ||
        feedProductionsFetching ||
        assignMutation.isPending) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}
      {/* Content */}
      <Paper
        sx={{
          p: 3,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid rgba(76, 175, 80, 0.3)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1 }}>
            <Typography variant="h6" sx={{ color: '#ffffff' }}>
              {showAllAssignments ? 'All Assignments' : 'Active Assignments'}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowAllAssignments(!showAllAssignments)}
              sx={{
                borderColor: '#66bb6a',
                color: '#66bb6a',
                '&:hover': {
                  borderColor: '#4caf50',
                  bgcolor: 'rgba(76, 175, 80, 0.08)',
                },
              }}
            >
              {showAllAssignments ? 'Show Active Only' : 'Show All'}
            </Button>

            <Autocomplete
              options={birds || []}
              getOptionLabel={(option) => option.name}
              value={birds.find((b) => b.id === birdFilter) || null}
              onChange={(_, newValue) => setBirdFilter(newValue?.id || null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Batch"
                  size="small"
                  sx={{ minWidth: 200 }}
                />
              )}
              size="small"
              sx={{ minWidth: 200 }}
            />

            <Autocomplete
              options={feedTypes || []}
              getOptionLabel={(option) => option.name}
              value={feedTypes?.find((t) => t.id === feedTypeFilter) || null}
              onChange={(_, newValue) => setFeedTypeFilter(newValue?.id || null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Feed Type"
                  size="small"
                  sx={{ minWidth: 200 }}
                />
              )}
              size="small"
              sx={{ minWidth: 200 }}
            />

            <Autocomplete
              options={feedBrands || []}
              getOptionLabel={(option) => option.name}
              value={feedBrands?.find((b) => b.id === feedBrandFilter) || null}
              onChange={(_, newValue) => setFeedBrandFilter(newValue?.id || null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Brand"
                  size="small"
                  sx={{ minWidth: 200 }}
                />
              )}
              size="small"
              sx={{ minWidth: 200 }}
            />

            {(feedTypeFilter || feedBrandFilter || birdFilter) && (
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setFeedTypeFilter(null);
                  setFeedBrandFilter(null);
                  setBirdFilter(null);
                }}
                sx={{
                  color: '#ffa726',
                  '&:hover': {
                    bgcolor: 'rgba(255, 152, 0, 0.08)',
                  },
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAssignDialog(true)}
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              color: '#ffffff',
            }}
          >
            Assign Feed
          </Button>
        </Box>

        <Box sx={{ flex: 1, width: '100%', minHeight: 0 }}>
          <DataGrid
            rows={assignments}
            columns={[
              {
                field: 'batch_identifier',
                headerName: 'Batch',
                flex: 1.2,
                minWidth: 150,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => (
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {params.row.birds?.name || '-'}
                    </Typography>
                  </Box>
                ),
              },
              {
                field: 'feed_type',
                headerName: 'Feed Type',
                flex: 1,
                minWidth: 120,
                headerAlign: 'center',
                align: 'center',
                valueGetter: (value, row) => row.feed_type?.name || '-',
              },
              {
                field: 'feed_brand',
                headerName: 'Brand',
                flex: 1,
                minWidth: 120,
                headerAlign: 'center',
                align: 'center',
                valueGetter: (value, row) => row.feed_brand?.name || '-',
              },
              {
                field: 'feed_source',
                headerName: 'Feed Source',
                flex: 1.3,
                minWidth: 160,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => {
                  if (params.row.feed_procurement) {
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Chip
                          label={`Lot #${params.row.feed_procurement.procurement_number}`}
                          size="small"
                          sx={{
                            bgcolor:
                              'linear-gradient(135deg, rgba(33, 150, 243, 0.2) 0%, rgba(33, 150, 243, 0.1) 100%)',
                            color: '#64b5f6',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            border: '1px solid rgba(33, 150, 243, 0.3)',
                            px: 1.5,
                            height: 26,
                            '& .MuiChip-label': {
                              px: 0.5,
                            },
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                          }}
                        >
                          {new Date(params.row.feed_procurement.delivery_date).toLocaleDateString(
                            'en-GB',
                            {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            }
                          )}
                        </Typography>
                      </Box>
                    );
                  } else if (params.row.feed_production) {
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Chip
                          label={`Batch #${params.row.feed_production.production_number}`}
                          size="small"
                          sx={{
                            bgcolor:
                              'linear-gradient(135deg, rgba(156, 39, 176, 0.2) 0%, rgba(156, 39, 176, 0.1) 100%)',
                            color: '#ba68c8',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            border: '1px solid rgba(156, 39, 176, 0.3)',
                            px: 1.5,
                            height: 26,
                            '& .MuiChip-label': {
                              px: 0.5,
                            },
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                          }}
                        >
                          {new Date(params.row.feed_production.production_date).toLocaleDateString(
                            'en-GB',
                            {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            }
                          )}
                        </Typography>
                      </Box>
                    );
                  }
                  return (
                    <Chip
                      label="Aggregate Stock"
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.08)',
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        px: 1,
                        height: 24,
                      }}
                    />
                  );
                },
              },
              {
                field: 'daily_consumption_kg',
                headerName: 'Daily Consumption',
                flex: 1,
                minWidth: 130,
                type: 'number',
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => (
                  <Chip
                    label={`${parseFloat(params.value).toFixed(1)} kg`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(76, 175, 80, 0.15)',
                      color: '#66bb6a',
                      fontWeight: 600,
                    }}
                  />
                ),
              },
              {
                field: 'weekly_consumption',
                headerName: 'Weekly Consumption',
                flex: 1,
                minWidth: 140,
                type: 'number',
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => (
                  <Chip
                    label={`${(parseFloat(params.row.daily_consumption_kg) * 7).toFixed(1)} kg`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 152, 0, 0.15)',
                      color: '#ffa726',
                      fontWeight: 600,
                    }}
                  />
                ),
              },
              {
                field: 'total_allocated_kg',
                headerName: 'Total Allocated',
                flex: 1,
                minWidth: 120,
                type: 'number',
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => (
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#ff9800' }}>
                    {parseFloat(params.value).toFixed(1)} kg
                  </Typography>
                ),
              },
              {
                field: 'total_consumed_kg',
                headerName: 'Total Consumed',
                flex: 1,
                minWidth: 120,
                type: 'number',
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => (
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  >
                    {parseFloat(params.value).toFixed(1)} kg
                  </Typography>
                ),
              },
              {
                field: 'assigned_date',
                headerName: 'Assigned Date',
                flex: 1,
                minWidth: 110,
                headerAlign: 'center',
                align: 'center',
                valueFormatter: (value) =>
                  new Date(value).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  }),
              },
              {
                field: 'deactivated_at',
                headerName: 'Deactivated Date',
                flex: 1,
                minWidth: 120,
                headerAlign: 'center',
                align: 'center',
                valueFormatter: (value) =>
                  value
                    ? new Date(value).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : '-',
              },
              {
                field: 'duration',
                headerName: 'Duration',
                flex: 0.9,
                minWidth: 100,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => {
                  const totalAllocated = parseFloat(params.row.total_allocated_kg);
                  const dailyConsumption = parseFloat(params.row.daily_consumption_kg);

                  const durationDays = Math.floor(totalAllocated / dailyConsumption);

                  return (
                    <Chip
                      label={`${durationDays} days`}
                      size="small"
                      sx={{
                        bgcolor: params.row.is_active
                          ? 'rgba(76, 175, 80, 0.15)'
                          : 'rgba(255, 255, 255, 0.05)',
                        color: params.row.is_active ? '#66bb6a' : 'rgba(255, 255, 255, 0.5)',
                        fontWeight: 600,
                      }}
                    />
                  );
                },
              },
              {
                field: 'is_active',
                headerName: 'Status',
                flex: 0.8,
                minWidth: 100,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) =>
                  params.value ? (
                    <Chip
                      label="Active"
                      color="success"
                      size="small"
                      sx={{ fontWeight: 600, minWidth: 70 }}
                    />
                  ) : (
                    <Chip
                      label="Inactive"
                      color="default"
                      size="small"
                      sx={{ fontWeight: 600, minWidth: 70 }}
                    />
                  ),
              },
              {
                field: 'actions',
                headerName: 'Actions',
                flex: 0.8,
                minWidth: 100,
                sortable: false,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(params.row)}
                      sx={{
                        color: '#ff9800',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 152, 0, 0.1)',
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {params.row.is_active && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setAssignmentToDeactivate(params.row);
                            setOpenDeactivateDialog(true);
                          }}
                          sx={{
                            color: '#ff9800',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 152, 0, 0.1)',
                            },
                          }}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setAssignmentToDelete(params.row);
                            setOpenDeleteDialog(true);
                          }}
                          sx={{
                            color: '#f44336',
                            '&:hover': {
                              backgroundColor: 'rgba(244, 67, 54, 0.1)',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                ),
              },
            ]}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            getRowHeight={() => 'auto'}
            disableRowSelectionOnClick
            loading={isLoading}
            sx={{
              border: 0,
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(76, 175, 80, 0.2)',
                py: 1.5,
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'rgba(76, 175, 80, 0.15)',
                borderBottom: '1px solid rgba(76, 175, 80, 0.3)',
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: '#ffffff',
                },
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  bgcolor: 'rgba(76, 175, 80, 0.08)',
                },
                '&.Mui-selected': {
                  bgcolor: 'rgba(76, 175, 80, 0.12)',
                  '&:hover': {
                    bgcolor: 'rgba(76, 175, 80, 0.16)',
                  },
                },
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid rgba(76, 175, 80, 0.3)',
                bgcolor: 'rgba(76, 175, 80, 0.05)',
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </Box>
      </Paper>

      {/* Assign Feed Dialog */}
      <Dialog
        open={openAssignDialog}
        onClose={() => setOpenAssignDialog(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(76, 175, 80, 0.3)' } } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(76, 175, 80, 0.3)', color: '#66bb6a' }}>
          Assign Feed to Batch
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={birds}
              getOptionLabel={(option) => option.name}
              value={birds.find((b) => b.id.toString() === assignForm.birds_id) || null}
              onChange={(_, newValue) =>
                setAssignForm({
                  ...assignForm,
                  birds_id: newValue?.id.toString() || '',
                })
              }
              renderInput={(params) => <TextField {...params} label="Select Birds" required />}
            />

            <Autocomplete
              options={feedTypes || []}
              getOptionLabel={(option) => option.name}
              value={feedTypes?.find((t) => t.id.toString() === assignForm.feed_type_id) || null}
              onChange={(_, newValue) =>
                setAssignForm({ ...assignForm, feed_type_id: newValue?.id.toString() || '' })
              }
              renderInput={(params) => <TextField {...params} label="Feed Type" required />}
            />

            <Autocomplete
              options={feedBrands || []}
              getOptionLabel={(option) => option.name}
              value={feedBrands?.find((b) => b.id.toString() === assignForm.feed_brand_id) || null}
              onChange={(_, newValue) =>
                setAssignForm({ ...assignForm, feed_brand_id: newValue?.id.toString() || '' })
              }
              renderInput={(params) => <TextField {...params} label="Feed Brand" required />}
            />

            <Autocomplete
              getOptionLabel={(option) => option.name}
              renderInput={(params) => <TextField {...params} label="Feed source" required />}
              value={assignForm.source}
              onChange={(_, newValue) =>
                setAssignForm({ ...assignForm, source: newValue || assignForm.source })
              }
              options={[
                {
                  name: 'Auto (aggregate stock)',
                  id: 'auto',
                },
                {
                  name: 'Procured (select lot)',
                  id: 'procurement',
                },
                { name: 'Produced (in-house)', id: 'production' },
              ]}
            />

            {assignForm.source.id === 'procurement' && (
              <Autocomplete
                options={(feedProcurements?.data || []).filter(
                  (p: any) => p.status === 'delivered'
                )}
                getOptionLabel={(option) =>
                  `Lot #${option.procurement_number} - ${option.feed_brand?.name}`
                }
                value={
                  feedProcurements?.data?.find(
                    (p) => p.id.toString() === assignForm.feed_procurement_id
                  ) || null
                }
                onChange={(_, newValue) =>
                  setAssignForm({
                    ...assignForm,
                    feed_procurement_id: newValue?.id.toString() || '',
                  })
                }
                renderInput={(params) => <TextField {...params} label="Select Procurement Lot" />}
              />
            )}

            {assignForm.source.id === 'production' && (
              <Autocomplete
                options={feedProductions || []}
                getOptionLabel={(option) =>
                  `Batch #${option.production_number} - (${option.quantity_produced_bags})`
                }
                value={
                  feedProductions?.find((p) => p.id.toString() === assignForm.feed_production_id) ||
                  null
                }
                onChange={(_, newValue) =>
                  setAssignForm({
                    ...assignForm,
                    feed_production_id: newValue?.id.toString() || '',
                  })
                }
                renderInput={(params) => <TextField {...params} label="Select Production Batch" />}
              />
            )}
            <Autocomplete
              options={[
                { label: 'Daily (ongoing)', value: 'daily' },
                { label: 'Weekly (ongoing)', value: 'weekly' },
                { label: 'One-time (single day)', value: 'once' },
              ]}
              getOptionLabel={(option) => option.label}
              value={
                assignForm.consumption_period === 'daily'
                  ? { label: 'Daily (ongoing)', value: 'daily' }
                  : assignForm.consumption_period === 'once'
                    ? { label: 'One-time (single day)', value: 'once' }
                    : { label: 'Weekly (ongoing)', value: 'weekly' }
              }
              onChange={(_, newValue) =>
                setAssignForm({
                  ...assignForm,
                  consumption_period: newValue?.value || 'weekly',
                })
              }
              renderInput={(params) => (
                <TextField {...params} label="Consumption Period" required />
              )}
            />

            <TextField
              label={`${
                assignForm.consumption_period === 'daily'
                  ? 'Daily'
                  : assignForm.consumption_period === 'once'
                    ? 'One-time'
                    : 'Weekly'
              } Consumption (kg)`}
              type="number"
              required
              value={assignForm.consumption_kg || ''}
              onChange={(e) => setAssignForm({ ...assignForm, consumption_kg: e.target.value })}
              helperText={
                assignForm.consumption_period === 'once'
                  ? 'Feed quantity for one day only'
                  : `Expected ${assignForm.consumption_period} feed consumption for this batch`
              }
            />

            <TextField
              label="Allocation Multiplier"
              type="number"
              required
              value={assignForm.allocation_multiplier || ''}
              onChange={(e) =>
                setAssignForm({ ...assignForm, allocation_multiplier: e.target.value })
              }
              helperText={`Initial stock allocated = consumption × ${assignForm.allocation_multiplier || '1'} = ${(parseFloat(assignForm.consumption_kg || '0') * parseFloat(assignForm.allocation_multiplier || '1')).toFixed(1)} kg`}
              slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
            />

            <TextField
              label="Assignment Date"
              type="date"
              required
              value={assignForm.assigned_date}
              onChange={(e) => setAssignForm({ ...assignForm, assigned_date: e.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="Date when this feed assignment becomes active"
            />

            <TextField
              label="Notes"
              multiline
              rows={3}
              value={assignForm.notes}
              onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
              placeholder="Optional notes about this feed assignment"
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: '1px solid rgba(76, 175, 80, 0.3)',
            backgroundColor: 'rgba(76, 175, 80, 0.05)',
          }}
        >
          <Button
            onClick={() => setOpenAssignDialog(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={assignMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              color: '#ffffff',
            }}
          >
            {assignMutation.isPending ? 'Processing...' : 'Assign Feed'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Feed Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(255, 152, 0, 0.3)' } } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 152, 0, 0.3)', color: '#ffa726' }}>
          Edit Feed Assignment
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Batch"
              value={editingAssignment?.birds?.name || ''}
              disabled
              fullWidth
            />

            <Autocomplete
              options={feedTypes || []}
              getOptionLabel={(option) => option.name}
              value={feedTypes?.find((t) => t.id.toString() === editForm.feed_type_id) || null}
              onChange={(_, newValue) =>
                setEditForm({ ...editForm, feed_type_id: newValue?.id.toString() || '' })
              }
              renderInput={(params) => <TextField {...params} label="Feed Type" required />}
            />

            <Autocomplete
              options={feedBrands || []}
              getOptionLabel={(option) => option.name}
              value={feedBrands?.find((b) => b.id.toString() === editForm.feed_brand_id) || null}
              onChange={(_, newValue) =>
                setEditForm({ ...editForm, feed_brand_id: newValue?.id.toString() || '' })
              }
              renderInput={(params) => <TextField {...params} label="Feed Brand" required />}
            />

            <Autocomplete
              getOptionLabel={(option) => option.name}
              renderInput={(params) => <TextField {...params} label="Feed source" required />}
              value={editForm.source}
              onChange={(_, newValue) =>
                setEditForm({ ...editForm, source: newValue || editForm.source })
              }
              options={[
                { name: 'Auto (aggregate stock)', id: 'auto' },
                { name: 'Procured (select lot)', id: 'procurement' },
                { name: 'Produced (in-house)', id: 'production' },
              ]}
            />

            {editForm.source.id === 'procurement' && (
              <Autocomplete
                options={(feedProcurements?.data || []).filter(
                  (p: any) => p.status === 'delivered'
                )}
                getOptionLabel={(option) =>
                  `Lot #${option.procurement_number} - ${option.feed_brand?.name}`
                }
                value={
                  feedProcurements?.data?.find(
                    (p) => p.id.toString() === editForm.feed_procurement_id
                  ) || null
                }
                onChange={(_, newValue) =>
                  setEditForm({
                    ...editForm,
                    feed_procurement_id: newValue?.id.toString() || '',
                  })
                }
                renderInput={(params) => <TextField {...params} label="Select Procurement Lot" />}
              />
            )}

            {editForm.source.id === 'production' && (
              <Autocomplete
                options={feedProductions || []}
                getOptionLabel={(option) =>
                  `Batch #${option.production_number} - (${option.quantity_produced_bags})`
                }
                value={
                  feedProductions?.find((p) => p.id.toString() === editForm.feed_production_id) ||
                  null
                }
                onChange={(_, newValue) =>
                  setEditForm({
                    ...editForm,
                    feed_production_id: newValue?.id.toString() || '',
                  })
                }
                renderInput={(params) => <TextField {...params} label="Select Production Batch" />}
              />
            )}

            <Autocomplete
              options={[
                { label: 'Daily (ongoing)', value: 'daily' },
                { label: 'Weekly (ongoing)', value: 'weekly' },
                { label: 'One-time (single day)', value: 'once' },
              ]}
              getOptionLabel={(option) => option.label}
              value={
                editForm.consumption_period === 'daily'
                  ? { label: 'Daily (ongoing)', value: 'daily' }
                  : editForm.consumption_period === 'once'
                    ? { label: 'One-time (single day)', value: 'once' }
                    : { label: 'Weekly (ongoing)', value: 'weekly' }
              }
              onChange={(_, newValue) =>
                setEditForm({
                  ...editForm,
                  consumption_period: newValue?.value || 'weekly',
                })
              }
              renderInput={(params) => (
                <TextField {...params} label="Consumption Period" required />
              )}
            />

            <TextField
              label={`${
                editForm.consumption_period === 'daily'
                  ? 'Daily'
                  : editForm.consumption_period === 'once'
                    ? 'One-time'
                    : 'Weekly'
              } Consumption (kg)`}
              type="number"
              required
              value={editForm.consumption_kg || ''}
              onChange={(e) => setEditForm({ ...editForm, consumption_kg: e.target.value })}
              helperText={
                editForm.consumption_period === 'once'
                  ? 'Feed quantity for one day only'
                  : `Expected ${editForm.consumption_period} feed consumption for this batch`
              }
            />

            <TextField
              label="Allocation Multiplier"
              type="number"
              required
              value={editForm.allocation_multiplier || ''}
              onChange={(e) => setEditForm({ ...editForm, allocation_multiplier: e.target.value })}
              helperText={`Additional stock allocated = consumption × ${editForm.allocation_multiplier || '1'} = ${(parseFloat(editForm.consumption_kg || '0') * parseFloat(editForm.allocation_multiplier || '1')).toFixed(1)} kg`}
              slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
            />

            <TextField
              label="Assignment Date"
              type="date"
              required
              value={editForm.assigned_date}
              onChange={(e) => setEditForm({ ...editForm, assigned_date: e.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="Date when this feed assignment becomes active"
            />

            <TextField
              label="Notes"
              multiline
              rows={3}
              value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              placeholder="Optional notes about this feed assignment"
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: '1px solid rgba(255, 152, 0, 0.3)',
            backgroundColor: 'rgba(255, 152, 0, 0.05)',
          }}
        >
          <Button
            onClick={() => setOpenEditDialog(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={updateMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)',
              color: '#ffffff',
            }}
          >
            {updateMutation.isPending ? 'Updating...' : 'Update Assignment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(244, 67, 54, 0.3)' } } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(244, 67, 54, 0.3)' }}>
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#f44336' }}>
            Delete Feed Assignment
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" paragraph sx={{ color: '#ffffff' }}>
            Are you sure you want to delete this feed assignment?
          </Typography>
          {assignmentToDelete && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                borderRadius: 2,
                border: '1px solid rgba(244, 67, 54, 0.3)',
              }}
            >
              <Typography variant="body2" gutterBottom sx={{ color: 'rgba(244, 67, 54, 0.8)' }}>
                Batch:
              </Typography>
              <Typography variant="body1" fontWeight="bold" gutterBottom sx={{ color: '#ffffff' }}>
                {assignmentToDelete.birds?.name || 'N/A'}
              </Typography>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ mt: 1, color: 'rgba(244, 67, 54, 0.8)' }}
              >
                Feed:
              </Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ color: '#ffffff' }}>
                {assignmentToDelete.feed_type?.name} - {assignmentToDelete.feed_brand?.name}
              </Typography>
            </Paper>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. The assignment will be permanently removed.
          </Alert>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            borderTop: '1px solid rgba(244, 67, 54, 0.3)',
            backgroundColor: 'rgba(244, 67, 54, 0.05)',
          }}
        >
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)' },
              '&:disabled': { background: 'rgba(244, 67, 54, 0.3)' },
            }}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog
        open={openDeactivateDialog}
        onClose={() => setOpenDeactivateDialog(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(255, 152, 0, 0.3)' } } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 152, 0, 0.3)' }}>
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#ff9800' }}>
            Deactivate Feed Assignment
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" paragraph sx={{ color: '#ffffff' }}>
            Are you sure you want to deactivate this feed assignment?
          </Typography>
          {assignmentToDeactivate && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                borderRadius: 2,
                border: '1px solid rgba(255, 152, 0, 0.3)',
              }}
            >
              <Typography variant="body2" gutterBottom sx={{ color: 'rgba(255, 152, 0, 0.8)' }}>
                Batch:
              </Typography>
              <Typography variant="body1" fontWeight="bold" gutterBottom sx={{ color: '#ffffff' }}>
                {assignmentToDeactivate.birds?.name || 'N/A'}
              </Typography>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ mt: 1, color: 'rgba(255, 152, 0, 0.8)' }}
              >
                Feed:
              </Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ color: '#ffffff' }}>
                {assignmentToDeactivate.feed_type?.name} - {assignmentToDeactivate.feed_brand?.name}
              </Typography>
            </Paper>
          )}
          <Alert severity="info" sx={{ mt: 2 }}>
            Deactivating will preserve the assignment history and unreserve any unused stock.
          </Alert>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            borderTop: '1px solid rgba(255, 152, 0, 0.3)',
            backgroundColor: 'rgba(255, 152, 0, 0.05)',
          }}
        >
          <Button
            onClick={() => setOpenDeactivateDialog(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDeactivate}
            disabled={deactivateMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)' },
              '&:disabled': { background: 'rgba(255, 152, 0, 0.3)' },
            }}
          >
            {deactivateMutation.isPending ? 'Deactivating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            minWidth: '400px',
            fontSize: '1rem',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeedBatchAssignmentsPage;
